import { useHistory } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip, Skeleton, Spin, Alert } from 'antd'
import { EyeIcon, TrashIcon, PencilIcon, DownloadIcon } from '@heroicons/react/outline'
import {
  DeleteDocumentUseCase,
  DeleteSignatureUseCase,
  DownloadDocumentUseCase,
  IDocument,
  IRenderDocumentPagesResponse,
  QuerySignaturesUseCase,
  RenderDocumentPagesUseCase,
} from '@axelity/actasign-sic'
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import { documentListState, documentRenderErrorsState, documentState, signatureListState, userInfoState, viewerState } from '../recoil/atoms'

type DocumentItemProps = {
  content?: IDocument
  canPrepare?: boolean
  canView?: boolean
  canRemove?: boolean
  canDownload?: boolean
  showStatus?: boolean
}

type TDocumentItemActionProps = {
  show?: boolean
  label: string
  clickAction?: () => void
  icon?: React.ReactNode
}

const DocumentItemAction: React.FunctionComponent<TDocumentItemActionProps> = ({
  label,
  clickAction,
  icon,
  show,
}: TDocumentItemActionProps) => {
  if (show) {
    return (
      <div
        className="flex flex-col items-center p-2 rounded-md text-xs font-semibold text-acs-blue-600 hover:bg-acs-blue-100 hover:text-acs-blue-700 hover:cursor-pointer"
        onClick={clickAction}
      >
        {icon}
        <p style={{ fontSize: '9px' }} className="mt-1">
          {label}
        </p>
      </div>
    )
  } else {
    return null
  }
}

const DocumentItem = ({
  content,
  canDownload = false,
  canPrepare = false,
  canRemove = false,
  canView = false,
  showStatus = true,
}: DocumentItemProps) => {
  const { t } = useTranslation(['documentItem'])

  const history = useHistory()

  const userInfo = useRecoilValue(userInfoState)

  const [documentList, setDocumentList] = useRecoilState(documentListState)
  const [documentRenderErrors, setDocumentRenderErrors] = useRecoilState(documentRenderErrorsState)
  const setRequestDocument = useSetRecoilState(documentState)
  const resetSignatures = useResetRecoilState(signatureListState)
  const setViewType = useSetRecoilState(viewerState)

  const [page, setPage] = useState<IRenderDocumentPagesResponse>(undefined)
  const [inProgress, setInProgress] = useState<boolean>(false)
  const [isProtected, setIsProtected] = useState<boolean>(false)
  const [renderError, setRenderError] = useState<string | null>(null)

  const addDocumentRenderError = (documentId: string) => {
    setDocumentRenderErrors((prev) => new Set(prev).add(documentId))
  }

  const removeDocumentRenderError = (documentId: string) => {
    setDocumentRenderErrors((prev) => {
      const newSet = new Set(prev)
      newSet.delete(documentId)
      return newSet
    })
  }

  const handlePrepareDocument = () => {
    resetSignatures()
    setRequestDocument(content)
    setViewType('CHANGE')
    history.push('/viewer')
  }

  const handleViewDocument = () => {
    resetSignatures()
    setRequestDocument(content)
    if (canPrepare) {
      setViewType('CHANGE')
    } else {
      setViewType('VIEW')
    }
    history.push('/viewer')
  }

  const handleDownloadDocument = () => {
    let filename: string = ''
    if (content.status === 'SIGNED') {
      const extension = content.filename.substring(content.filename.length - 4)
      const baseName = content.filename.substring(0, content.filename.length - 4)
      filename = filename.concat(baseName, userInfo.otherSettings.suffixSignedDocuments, extension)
    } else {
      filename = content.filename
    }

    new DownloadDocumentUseCase({
      documentId: content._id,
    })
      .download()
      .then((blob) => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.style.display = 'none'
        a.click()
        a.remove()
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const removeDocument = async () => {
    const signatures = await new QuerySignaturesUseCase().query({
      document: content._id,
    })

    if (signatures) {
      for (let i = 0; i < signatures.length; i++) {
        try {
          await new DeleteSignatureUseCase({
            signatureId: signatures[i]._id,
          }).delete()
        } catch (error) {
          console.error(`Cannot delete signature ${signatures[i]._id}: ${error.message}`)
        }
      }
    }

    await new DeleteDocumentUseCase({
      documentId: content._id,
    }).delete()

    // Remove from error tracking if it was there
    removeDocumentRenderError(content._id)

    setDocumentList([
      ...documentList.filter((doc) => {
        return doc._id !== content._id
      }),
    ])
  }

  const handleRemoveDocument = () => {
    setInProgress(true)
    removeDocument()
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        setInProgress(false)
      })
  }

  useEffect(() => {
    if (content) {
      console.log(`[DocumentItem] useEffect triggered for document: ${content.displayName} (ID: ${content._id})`)

      // First, try to render the document
      // If rendering fails, we'll download and check if it's protected
      console.log(`[DocumentItem] Attempting to render pages for: ${content.displayName}`)

      new RenderDocumentPagesUseCase({
        documentId: content._id,
      })
        .render({
          dpi: 72,
          fileType: 'png',
          setOfPages: '1',
        })
        .then((pages) => {
          console.log(`[DocumentItem] ✅ Pages rendered successfully for: ${content.displayName}`)
          setPage(pages[0])
          setIsProtected(false)
          removeDocumentRenderError(content._id)
        })
        .catch(async (renderError) => {
          console.error(`[DocumentItem] ❌ Render pages failed for: ${content.displayName}`, renderError)
          console.error(`[DocumentItem] Error status:`, renderError?.status)
          console.error(`[DocumentItem] Error message:`, renderError?.message)

          // When rendering fails, download the document and check if it's actually protected
          console.log(`[DocumentItem] Downloading document to verify protection status...`)

          try {
            const blob = await new DownloadDocumentUseCase({
              documentId: content._id,
            }).download()

            console.log(`[DocumentItem] Document downloaded successfully, checking protection...`)

            // Convert blob to ArrayBuffer for PDF.js
            const arrayBuffer = await blob.arrayBuffer()

            // Dynamically import PDF.js to check protection
            const pdfjsLib = await import('pdfjs-dist')

            try {
              // Attempt to load the PDF without a password
              const loadingTask = pdfjsLib.getDocument({
                data: arrayBuffer,
                password: '', // Try with empty password first
              })

              await loadingTask.promise

              // If we get here, the document loaded successfully (not protected)
              // But rendering still failed for some other reason
              console.log(`[DocumentItem] Document is NOT protected but rendering failed due to other reasons`)
              console.error('[DocumentItem] Unexpected render failure for unprotected document:', renderError)

              setIsProtected(false)
              setPage(undefined)
              setRenderError(renderError?.message || 'Failed to render document')
              addDocumentRenderError(content._id)

            } catch (pdfError: any) {
              // Check if error is password-related
              if (pdfError.name === 'PasswordException' ||
                  pdfError.message?.includes('password') ||
                  pdfError.message?.includes('encrypted')) {
                console.log(`[DocumentItem] ✅ Confirmed: Document "${content.displayName}" is password-protected/encrypted`)
                setIsProtected(true)
                setPage(undefined)
              } else {
                // Some other PDF error
                console.error(`[DocumentItem] PDF loading failed with unexpected error:`, pdfError)
                setIsProtected(true) // Assume protected to be safe
                setPage(undefined)
              }
            }

          } catch (downloadError) {
            console.error(`[DocumentItem] Failed to download document for verification:`, downloadError)

            // If we can't download, fall back to checking the render error message
            const errorMessage = renderError?.message || ''
            const isLikelyProtected = errorMessage.toLowerCase().includes('password') ||
                                      errorMessage.toLowerCase().includes('encrypted') ||
                                      errorMessage.toLowerCase().includes('protected') ||
                                      renderError?.status === 500

            console.log(`[DocumentItem] Fallback detection - isLikelyProtected: ${isLikelyProtected}`)
            setIsProtected(isLikelyProtected)
            setPage(undefined)
          }
        })
    }
  }, [content])

  return (
    <div className="container w-full md:w-1/2 lg:w-4/10 m-1 border-2 rounded-md shadow-md bg-white border-gray-100 hover:border-acs-blue-600">
      <div className="w-full mt-4 px-4">
        <div className="flex w-full justify-between items-center">
          {/**
           * display name
           */}
          <Tooltip placement="top" title={content.displayName}>
            <p className="text-sm font-semibold whitespace-nowrap overflow-hidden overflow-ellipsis cursor-pointer">
              {content.displayName}
            </p>
          </Tooltip>

          {/**
           * Status badges
           */}
          <div className="flex justify-end items-center gap-1">
            {isProtected && (
              <Tooltip placement="top" title={t('documentList:pdfVerification.protectedBadge')}>
                <div
                  style={{ fontSize: '10px' }}
                  className="rounded-md px-1 py-1 text-xs font-semibold bg-red-100 text-red-700 border border-red-300"
                >
                  {t('documentItem:protected')}
                </div>
              </Tooltip>
            )}
            {showStatus && (
              <div
                style={{ fontSize: '10px' }}
                className={`rounded-md px-1 py-1 text-xs font-semibold ${
                  content.status === 'ATTACHED' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                }`}
              >
                {t(`documentItem:status.${content.status.toLowerCase()}`)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/**
       * Document preview (1st page)
       */}
      <Spin spinning={inProgress} size="large">
        {renderError ? (
          <div className="w-full px-6 py-3">
            <Alert
              message={t('documentItem:renderError.title')}
              description={t('documentItem:renderError.description')}
              type="error"
              showIcon
            />
          </div>
        ) : page ? (
          <img
            className="w-full px-6 py-3 hover:cursor-pointer"
            src={page.dataUrl}
            alt={content.displayName}
            onClick={handleViewDocument}
          />
        ) : (
          <div className="w-full h-auto px-6 py-3">
            {/*
          <img className="" src={`https://via.placeholder.com/595x842.png?text=...loading...`} alt="document" />
        */}
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        )}
      </Spin>

      {/**
       * Actions
       */}
      <div className="flex justify-evenly w-full">
        <DocumentItemAction
          show={canView}
          label={t('action.view')}
          clickAction={handleViewDocument}
          icon={<EyeIcon className="w-5 h-5" />}
        />
        <DocumentItemAction
          show={canPrepare}
          label={t('action.prepare')}
          clickAction={handlePrepareDocument}
          icon={<PencilIcon className="w-5 h-5" />}
        />
        <DocumentItemAction
          show={canDownload}
          label={t('action.download')}
          clickAction={handleDownloadDocument}
          icon={<DownloadIcon className="w-5 h-5" />}
        />
        <DocumentItemAction
          show={canRemove}
          label={t('action.remove')}
          clickAction={handleRemoveDocument}
          icon={<TrashIcon className="w-5 h-5" />}
        />
      </div>
    </div>
  )
}

export default DocumentItem
